/**
 * Case Intelligence Chat API Service
 */

import { getAuthToken } from '../utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export interface ToolCall {
  tool_name: string;
  parameters: Record<string, any>;
  result: Record<string, any>;
}

export interface Citation {
  source_type: string;
  source_name: string;
  details: string;
  excerpt?: string;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  tool_calls?: ToolCall[];
  citations?: Citation[];
}

export interface ChatResponse {
  response: string;
  tool_calls: ToolCall[];
  citations: Citation[];
  suggested_followups: string[];
  conversation_id: string;
  tokens_used?: number;
}

export interface ToolInfo {
  name: string;
  description: string;
  parameters: Record<string, any>;
}

// =============================================================================
// PREPARE-CLAIM PROPOSALS (Forensic Assistant "prep the claim" tool)
// =============================================================================
// Shapes returned in the `result` of a `prepare_claim` tool call. The
// accountant can deep-link override proposals into the attested override
// modal, and file missing-document / discrepancy proposals as tracked issues.

/** An AI-proposed engine-field override. `suggested_value` is intentionally
 * often null — the AI must NOT invent dollar values; the human enters them. */
export interface OverrideProposal {
  kind: 'override';
  engine_id: string;
  field_id: string;
  field_label: string;
  current_value: any | null;
  suggested_value: any | null;
  source: string | null;
  rationale: string;
}

/** An AI-flagged missing supporting document. */
export interface MissingDocumentProposal {
  kind: 'missing_document';
  doc_type: string;
  display_name: string;
  priority: string;
  rationale: string;
}

/** An AI-flagged cross-document discrepancy. */
export interface DiscrepancyProposal {
  kind: 'discrepancy';
  check_type: string;
  severity: string;
  message: string;
  recommendation: string | null;
  source_documents: string[];
}

/** Union of the two proposal kinds that can be filed as issues. */
export type FileableProposal = MissingDocumentProposal | DiscrepancyProposal;

/** An AI-proposed transaction categorization. `suggested_category` may be null —
 * when it is, the accountant must supply the category before applying. The
 * `reason` the accountant types is the audit attestation; it is NOT prefilled. */
export interface TransactionProposal {
  kind: 'transaction';
  transaction_id: string;
  date: string | null;
  description: string;
  amount: number | null;
  current_category: string | null;
  suggested_category: string | null;
  confidence: number | null;
  reasoning: string | null;
}

/** An AI-flagged anomaly the accountant can triage (approve resolution). */
export interface AnomalyProposal {
  kind: 'anomaly';
  anomaly_id: string;
  anomaly_type: string | null;
  severity: string; // 'high' | 'critical'
  title: string;
  description: string;
  explanation: string | null;
  recommendation: string | null;
}

/** The full `result` payload of a `prepare_claim` tool call. */
export interface PrepareClaimResult {
  report_id: string;
  ai_audit_id: string;
  override_proposals: OverrideProposal[];
  missing_documents: MissingDocumentProposal[];
  discrepancies: DiscrepancyProposal[];
  transaction_proposals: TransactionProposal[];
  anomaly_proposals: AnomalyProposal[];
  summary: {
    override_count: number;
    missing_count: number;
    discrepancy_count: number;
    transaction_count: number;
    anomaly_count: number;
  };
}

/** Response from POST /intelligence/proposals/file-issues. */
export interface FileIssuesResponse {
  created: string[];
  duplicates: string[];
  errors: string[];
}

class IntelligenceApi {
  private async getHeaders(): Promise<HeadersInit> {
    const token = await getAuthToken();
    if (!token) {
      throw new Error('Not authenticated: unable to retrieve auth token');
    }
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }

  async sendMessage(
    reportId: string,
    message: string,
    conversationId?: string,
    voiceMode: boolean = false,
    toolScope?: string
  ): Promise<ChatResponse> {
    const headers = await this.getHeaders();
    const response = await fetch(`${BACKEND_URL}/api/v1/intelligence/chat`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        report_id: reportId,
        message,
        conversation_id: conversationId,
        voice_mode: voiceMode,
        tool_scope: toolScope
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || `Chat failed: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * File missing-document / discrepancy proposals (from a `prepare_claim`
   * tool result) as tracked issues on the report. The `proposals` array
   * carries the SAME proposal objects from the tool result, including their
   * `kind` discriminator — the backend routes each kind accordingly.
   */
  async fileIssues(
    reportId: string,
    proposals: any[],
  ): Promise<FileIssuesResponse> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${BACKEND_URL}/api/v1/intelligence/proposals/file-issues`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({
          report_id: reportId,
          proposals,
        }),
      },
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || `Failed to file issues: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Apply a transaction categorization proposed by the Forensic Assistant.
   * `reason` is the accountant's audit attestation (backend requires length
   * >= 10). Re-run the reconciliation engines afterward to refresh totals.
   */
  async reclassifyTransaction(
    reportId: string,
    transactionId: string,
    newCategory: string,
    reason: string,
  ): Promise<any> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${BACKEND_URL}/api/v1/reconciliation/transactions/${reportId}/${transactionId}/reclassify`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({
          new_category: newCategory,
          reason,
        }),
      },
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error.detail || `Failed to reclassify transaction: ${response.statusText}`,
      );
    }

    return response.json();
  }

  /**
   * Triage an anomaly flagged by the Forensic Assistant. `actionType` is the
   * accountant action (e.g. 'approve_resolution'); `note` is optional context.
   */
  async resolveAnomaly(
    reportId: string,
    anomalyId: string,
    actionType: string,
    note: string,
  ): Promise<any> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${BACKEND_URL}/api/v1/dashboard/anomaly-review/${reportId}/accountant-action`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({
          anomaly_id: anomalyId,
          action_type: actionType,
          note,
        }),
      },
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error.detail || `Failed to resolve anomaly: ${response.statusText}`,
      );
    }

    return response.json();
  }

  async getConversationHistory(conversationId: string): Promise<ChatMessage[]> {
    const headers = await this.getHeaders();
    const response = await fetch(
      `${BACKEND_URL}/api/v1/intelligence/conversations/${conversationId}`,
      { headers }
    );

    if (!response.ok) {
      if (response.status === 404) return [];
      throw new Error(`Failed to fetch history: ${response.statusText}`);
    }

    const data = await response.json();
    return data.messages || [];
  }

  async getAvailableTools(): Promise<ToolInfo[]> {
    const headers = await this.getHeaders();
    const response = await fetch(`${BACKEND_URL}/api/v1/intelligence/tools`, { headers });

    if (!response.ok) {
      throw new Error(`Failed to fetch tools: ${response.statusText}`);
    }

    const data = await response.json();
    return data.tools || [];
  }

  async healthCheck(): Promise<{ status: string; llm_configured: boolean }> {
    const response = await fetch(`${BACKEND_URL}/api/v1/intelligence/health`);
    if (!response.ok) {
      throw new Error('Intelligence service unavailable');
    }
    return response.json();
  }
}

export const intelligenceApi = new IntelligenceApi();
export default intelligenceApi;
